/**
 * Window Bridge for Builder Service
 * 
 * Bridges builder service functions to the window object for cross-context communication
 * in Office Add-ins (dialog ↔ taskpane/commands runtime).
 * 
 * This allows the Planning Modal (or any dialog) to access builder service functions
 * without direct imports. All heavy lifting (LLM, HTTP, state) happens in taskpane.
 */

import {
  initializeBuilderService,
  startBuild,
  startBuildImmediately,
  pauseBuild,
  resumeBuild,
  cancelBuild,
  getBuildState,
  getBuildMessages,
  isBuilding,
  shouldPause,
  shouldCancel,
  isBuilderServiceInitialized,
  registerProgressCallbacks,
  clearBuild,
  getQueueState,
  getQueuePosition,
  promoteQueuedBuild,
  dequeueByPlanId,
  clearQueue,
  markBuildAsSeen,
  getUnseenCompletedCount,
  type BuildProgress,
  type BuildStatus,
  type BuildError,
  type BuildProgressCallbacks,
  type SectionProgress,
  type BuildConversationMessage,
  type QueuedBuildEntry,
} from "../builderService";
import { backendClient } from "../../../services/communication/backendClient";
import type { PresentationPlan } from "@carousel-office/shared/types/planning";

// ═══════════════════════════════════════════════════════════════════════════
// API RESPONSE TYPES
// ═══════════════════════════════════════════════════════════════════════════

/** Response from /plans/{planId}/build/state endpoint */
interface BuildStateResponse {
  planId: string;
  status: string;
  currentSection?: number;
  currentSlide?: number;
  progress?: number;
  checkpointId?: string;
  errors?: Array<{ message: string; timestamp: string }>;
}

/** Response from /plans/{planId}/build/rollback endpoint */
interface RollbackResponse {
  success: boolean;
  message: string;
  restoredCheckpointId: string;
}

/** Response from /plans/{planId}/build/checkpoints endpoint */
interface CheckpointsResponse {
  checkpoints: Array<{
    id: string;
    timestamp: string;
    sectionIndex: number;
    slideIndex: number;
    description?: string;
  }>;
}

// ═══════════════════════════════════════════════════════════════════════════
// PROGRESS UPDATE CALLBACK MANAGEMENT
// ═══════════════════════════════════════════════════════════════════════════

type ProgressUpdateCallback = (progress: BuildProgress) => void;
type StatusChangeCallback = (status: BuildStatus, previousStatus: BuildStatus) => void;
type ErrorCallback = (error: BuildError) => void;

const progressUpdateCallbacks = new Set<ProgressUpdateCallback>();
const statusChangeCallbacks = new Set<StatusChangeCallback>();
const errorCallbacks = new Set<ErrorCallback>();

// Master unsubscribe from builderService
let masterUnsubscribe: (() => void) | null = null;

/**
 * Ensure we have a single subscription to builderService that fans out to all window callbacks
 */
function ensureMasterSubscription(): void {
  if (masterUnsubscribe) return;
  
  masterUnsubscribe = registerProgressCallbacks({
    onProgressUpdate: (progress) => {
      progressUpdateCallbacks.forEach(cb => {
        try {
          cb(progress);
        } catch (e) {
          console.error('[BuilderWindowBridge] Error in progress callback:', e);
        }
      });
    },
    onStatusChange: (status, previousStatus) => {
      statusChangeCallbacks.forEach(cb => {
        try {
          cb(status, previousStatus);
        } catch (e) {
          console.error('[BuilderWindowBridge] Error in status callback:', e);
        }
      });
    },
    onError: (error) => {
      errorCallbacks.forEach(cb => {
        try {
          cb(error);
        } catch (e) {
          console.error('[BuilderWindowBridge] Error in error callback:', e);
        }
      });
    },
  });
  
  console.log('[BuilderWindowBridge] Master subscription created');
}

/**
 * Register a callback to receive progress updates
 * @returns Unsubscribe function
 */
function registerProgressUpdateCallback(callback: ProgressUpdateCallback): () => void {
  ensureMasterSubscription();
  progressUpdateCallbacks.add(callback);
  console.log(`[BuilderWindowBridge] Registered progress callback (total: ${progressUpdateCallbacks.size})`);
  
  return () => {
    progressUpdateCallbacks.delete(callback);
    console.log(`[BuilderWindowBridge] Unregistered progress callback (total: ${progressUpdateCallbacks.size})`);
  };
}

/**
 * Register a callback to receive status changes
 * @returns Unsubscribe function
 */
function registerStatusChangeCallback(callback: StatusChangeCallback): () => void {
  ensureMasterSubscription();
  statusChangeCallbacks.add(callback);
  
  return () => {
    statusChangeCallbacks.delete(callback);
  };
}

/**
 * Register a callback to receive errors
 * @returns Unsubscribe function
 */
function registerErrorCallback(callback: ErrorCallback): () => void {
  ensureMasterSubscription();
  errorCallbacks.add(callback);
  
  return () => {
    errorCallbacks.delete(callback);
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// WINDOW BRIDGE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Bridges builder service functions to the window object
 * 
 * @param window - The window object to attach functions to (defaults to global window)
 */
export function bridgeBuilderServiceToWindow(window: Window = globalThis.window): void {
  // =========================================================================
  // Service Lifecycle
  // =========================================================================
  
  /** Initialize the builder service (call this before using other functions) */
  window.__initializeBuilderService = initializeBuilderService;
  
  /** Check if builder service is initialized */
  window.__isBuilderServiceInitialized = isBuilderServiceInitialized;
  
  // Auto-initialize the builder service when bridge is created
  console.log('[BuilderWindowBridge] Auto-initializing builder service...');
  initializeBuilderService()
    .then(() => {
      console.log('[BuilderWindowBridge] Builder service auto-initialized successfully');
    })
    .catch((error) => {
      console.error('[BuilderWindowBridge] Failed to auto-initialize builder service:', error);
      console.log('[BuilderWindowBridge] User may need to call __initializeBuilderService() manually');
    });
  
  // =========================================================================
  // Build Lifecycle
  // =========================================================================
  
  /**
   * Start a new build
   * @param planId - The plan ID to build
   * @param plan - The plan object with sections and slides
   * @param styleGuidedocumentId - ID of the style guide deck
   * @param styleGuidePath - Optional path to style guide file
   */
  window.__startBuild = async (
    planId: string,
    plan: PresentationPlan,
    styleGuidedocumentId: string,
    styleGuidePath?: string,
    slidesBase64?: string[]
  ): Promise<BuildProgress> => {
    console.log('[BuilderWindowBridge] __startBuild called with:', {
      planId,
      planSections: plan?.sections?.length,
      styleGuidedocumentId,
      styleGuidePath,
      slidesBase64Count: slidesBase64?.length || 0,
    });
    console.log('[BuilderWindowBridge] isBuilderServiceInitialized:', isBuilderServiceInitialized());
    
    // Auto-initialize if not already initialized
    if (!isBuilderServiceInitialized()) {
      console.log('[BuilderWindowBridge] Builder service not initialized, initializing now...');
      try {
        await initializeBuilderService();
        console.log('[BuilderWindowBridge] Builder service initialized successfully');
      } catch (initError) {
        console.error('[BuilderWindowBridge] Failed to initialize builder service:', initError);
        throw new Error(`Failed to initialize builder service: ${initError instanceof Error ? initError.message : String(initError)}`);
      }
    }
    
    try {
      const result = await startBuild(planId, plan, styleGuidedocumentId, styleGuidePath, slidesBase64);
      console.log('[BuilderWindowBridge] startBuild returned:', result);
      return result;
    } catch (error) {
      console.error('[BuilderWindowBridge] startBuild threw error:', error);
      throw error;
    }
  };
  
  /** Pause the current build */
  window.__pauseBuild = pauseBuild;
  
  /** Resume a paused build */
  window.__resumeBuild = resumeBuild;
  
  /** Cancel the current build */
  window.__cancelBuild = cancelBuild;
  
  /** Clear the current build state (after completion) */
  window.__clearBuild = clearBuild;
  
  // =========================================================================
  // State Access
  // =========================================================================
  
  /** Get current build state (poll this for progress updates) */
  window.__getBuildState = getBuildState;
  
  /** Check if a build is currently active */
  window.__isBuilding = isBuilding;
  
  /** Check if pause has been requested */
  window.__shouldPauseBuild = shouldPause;
  
  /** Check if cancel has been requested */
  window.__shouldCancelBuild = shouldCancel;
  
  // =========================================================================
  // Progress Callbacks (for real-time updates)
  // =========================================================================
  
  /**
   * Register a callback to receive progress updates
   * @returns Unsubscribe function
   */
  window.__onBuildProgress = registerProgressUpdateCallback;
  
  /**
   * Register a callback to receive status changes
   * @returns Unsubscribe function
   */
  window.__onBuildStatusChange = registerStatusChangeCallback;
  
  /**
   * Register a callback to receive build errors
   * @returns Unsubscribe function
   */
  window.__onBuildError = registerErrorCallback;
  
  // =========================================================================
  // Build API Methods (direct backend calls)
  // =========================================================================
  
  /**
   * Get build state from backend (for resuming)
   */
  window.__builderApi_getState = async (planId: string): Promise<BuildStateResponse> => {
    const response = await fetch(`${backendClient.baseUrl}/plans/${planId}/build/state`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to get build state: ${response.statusText}`);
    }
    
    return response.json();
  };
  
  /**
   * Rollback to a checkpoint
   */
  window.__builderApi_rollback = async (planId: string, checkpointId: string): Promise<RollbackResponse> => {
    const response = await fetch(`${backendClient.baseUrl}/plans/${planId}/build/rollback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ checkpoint_id: checkpointId }),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to rollback: ${response.statusText}`);
    }
    
    return response.json();
  };
  
  /**
   * Get available checkpoints for a plan
   */
  window.__builderApi_getCheckpoints = async (planId: string): Promise<CheckpointsResponse> => {
    const response = await fetch(`${backendClient.baseUrl}/plans/${planId}/build/checkpoints`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to get checkpoints: ${response.statusText}`);
    }
    
    return response.json();
  };
  
  // =========================================================================
  // Build Jobs List (for UI)
  // =========================================================================
  
  /**
   * Helper to extract current section and activity text from BuildProgress
   */
  function extractProgressDetails(progress: BuildProgress): { currentSection?: string; currentActivity?: string } {
    const currentSectionObj = progress.sections?.[progress.currentSectionIndex];
    const currentSection = currentSectionObj?.sectionName;
    const currentActivity = progress.currentPhase 
      ? `${progress.currentPhase}${progress.currentArea ? `: ${progress.currentArea}` : ''}`
      : progress.currentArea;
    return { currentSection, currentActivity };
  }
  
  /**
   * Helper to convert BuildProgress to BuildJob format for UI
   */
  function buildProgressToJob(progress: BuildProgress) {
    const { currentSection, currentActivity } = extractProgressDetails(progress);
    
    return {
      planId: progress.planId,
      planTitle: progress.planTitle,
      status: progress.status,
      slideCount: progress.totalSlides,
      slidesBuilt: progress.slidesBuilt,
      currentSection,
      currentActivity,
      sections: progress.sections || [],
      progress: progress.percentComplete,
      startedAt: progress.startedAt,
      completedAt: progress.completedAt,
      errors: progress.errors,
      seen: progress.seen,
    };
  }
  
  /**
   * Helper to convert QueuedBuildEntry to BuildJob format for UI
   */
  function queuedEntryToJob(entry: QueuedBuildEntry) {
    return {
      planId: entry.planId,
      planTitle: entry.planTitle,
      status: 'queued' as BuildStatus,
      slideCount: entry.totalSlides,
      slidesBuilt: 0,
      currentSection: undefined,
      currentActivity: undefined,
      sections: [],
      progress: 0,
      startedAt: undefined,
      completedAt: undefined,
      errors: [] as BuildError[],
      seen: undefined as boolean | undefined,
    };
  }
  
  /**
   * Get all active build jobs (running, paused, queued)
   * Returns both the active build and all queued builds
   */
  window.__getActiveBuildJobs = async () => {
    const { active, queue, completed } = getQueueState();
    const jobs = [];
    
    // Active build first (if any)
    if (active) {
      jobs.push(buildProgressToJob(active));
    }
    
    // Then queued builds
    for (const entry of queue) {
      jobs.push(queuedEntryToJob(entry));
    }
    
    // Also include recently completed/paused builds from persistence
    // (these are restored from disk on startup)
    for (const build of completed) {
      // Only include if not already in jobs (by planId)
      if (!jobs.some(j => j.planId === build.planId)) {
        jobs.push(buildProgressToJob(build));
      }
    }
    
    return jobs;
  };
  
  /**
   * Subscribe to build updates (for real-time UI updates)
   * Returns unsubscribe function
   */
  window.__subscribeToBuildUpdates = (callback) => {
    return registerProgressUpdateCallback((progress) => {
      const { currentSection, currentActivity } = extractProgressDetails(progress);
      
      // Get the latest message for this build (if any)
      const messages = getBuildMessages(progress.planId);
      const latestMessage = messages.length > 0 ? messages[messages.length - 1] : undefined;
        
      callback({
        planId: progress.planId,
        planTitle: progress.planTitle,
        status: progress.status,
        progress: progress.percentComplete,
        slidesBuilt: progress.slidesBuilt,
        totalSlides: progress.totalSlides,
        currentSection,
        currentActivity,
        sections: progress.sections,
        message: latestMessage,
      });
    });
  };
  
  /**
   * Get conversation messages for a build (for detail view)
   */
  window.__getBuildMessages = async (planId: string) => {
    return getBuildMessages(planId);
  };
  
  // =========================================================================
  // Queue Management
  // =========================================================================
  
  /**
   * Promote a queued build to active (pauses current if running)
   */
  window.__promoteQueuedBuild = async (planId: string) => {
    const result = await promoteQueuedBuild(planId);
    return result ? buildProgressToJob(result) : null;
  };
  
  /**
   * Get queue position for a plan (1-based for UI)
   */
  window.__getQueuePosition = (planId: string) => {
    return getQueuePosition(planId);
  };
  
  /**
   * Remove a build from the queue
   */
  window.__removeFromQueue = async (planId: string) => {
    return dequeueByPlanId(planId);
  };
  
  /**
   * Clear the entire queue
   */
  window.__clearQueue = async () => {
    await clearQueue();
  };
  
  /**
   * Get the current queue state
   */
  window.__getQueueState = () => {
    const state = getQueueState();
    return {
      active: state.active ? buildProgressToJob(state.active) : null,
      queue: state.queue.map(e => queuedEntryToJob(e)),
      completed: state.completed.map(b => buildProgressToJob(b)),
    };
  };
  
  /**
   * Start a build immediately, pausing any active build
   */
  window.__startBuildImmediately = async (
    planId: string,
    plan: PresentationPlan,
    styleGuidedocumentId: string,
    styleGuidePath?: string,
    slidesBase64?: string[]
  ): Promise<BuildProgress> => {
    console.log('[BuilderWindowBridge] __startBuildImmediately called');
    
    // Auto-initialize if needed
    if (!isBuilderServiceInitialized()) {
      await initializeBuilderService();
    }
    
    return startBuildImmediately(planId, plan, styleGuidedocumentId, styleGuidePath, slidesBase64);
  };
  
  /**
   * Mark a completed build as seen (removes "New" badge)
   */
  window.__markBuildAsSeen = (planId: string): boolean => {
    return markBuildAsSeen(planId);
  };
  
  /**
   * Get count of unseen completed builds
   */
  window.__getUnseenCompletedCount = (): number => {
    return getUnseenCompletedCount();
  };

  // =========================================================================
  // Logging
  // =========================================================================
  
  const exposedFunctions = {
    // Service
    hasInitialize: !!window.__initializeBuilderService,
    hasIsInitialized: !!window.__isBuilderServiceInitialized,
    // Lifecycle
    hasStartBuild: !!window.__startBuild,
    hasPauseBuild: !!window.__pauseBuild,
    hasResumeBuild: !!window.__resumeBuild,
    hasCancelBuild: !!window.__cancelBuild,
    hasClearBuild: !!window.__clearBuild,
    // State
    hasGetBuildState: !!window.__getBuildState,
    hasIsBuilding: !!window.__isBuilding,
    hasShouldPause: !!window.__shouldPauseBuild,
    hasShouldCancel: !!window.__shouldCancelBuild,
    // Callbacks
    hasOnProgress: !!window.__onBuildProgress,
    hasOnStatusChange: !!window.__onBuildStatusChange,
    hasOnError: !!window.__onBuildError,
    // API
    hasApiGetState: !!window.__builderApi_getState,
    hasApiRollback: !!window.__builderApi_rollback,
    hasApiCheckpoints: !!window.__builderApi_getCheckpoints,
    // Queue Management
    hasPromoteQueuedBuild: !!window.__promoteQueuedBuild,
    hasGetQueuePosition: !!window.__getQueuePosition,
    hasRemoveFromQueue: !!window.__removeFromQueue,
    hasClearQueue: !!window.__clearQueue,
    hasGetQueueState: !!window.__getQueueState,
    hasStartBuildImmediately: !!window.__startBuildImmediately,
    hasMarkBuildAsSeen: !!window.__markBuildAsSeen,
    hasGetUnseenCompletedCount: !!window.__getUnseenCompletedCount,
  };

  console.log('[BuilderWindowBridge] Builder service functions bridged to window:', exposedFunctions);

  // Verify all functions were exposed successfully
  const missingFunctions = Object.entries(exposedFunctions)
    .filter(([_, exists]) => !exists)
    .map(([name]) => name);

  if (missingFunctions.length > 0) {
    console.warn('[BuilderWindowBridge] Some builder service functions failed to bridge:', missingFunctions);
  }
}

/**
 * Verifies that all builder service functions are bridged to window
 * @returns true if all functions are available, false otherwise
 */
export function verifyBuilderServiceBridge(window: Window = globalThis.window): boolean {
  const required = [
    '__initializeBuilderService',
    '__startBuild',
    '__pauseBuild',
    '__resumeBuild',
    '__cancelBuild',
    '__getBuildState',
    '__isBuilding',
  ] as const;

  const missing = required.filter(name => !(window as any)[name]);

  if (missing.length > 0) {
    console.warn('[BuilderWindowBridge] Missing builder service functions in bridge:', missing);
    return false;
  }

  return true;
}

// ═══════════════════════════════════════════════════════════════════════════
// TYPE DECLARATIONS FOR WINDOW
// ═══════════════════════════════════════════════════════════════════════════

// Extend Window interface with builder functions
declare global {
  interface Window {
    // Service lifecycle
    __initializeBuilderService: typeof initializeBuilderService;
    __isBuilderServiceInitialized: typeof isBuilderServiceInitialized;
    
    // Build lifecycle
    __startBuild: (
      planId: string,
      plan: PresentationPlan,
      styleGuidedocumentId: string,
      styleGuidePath?: string,
      slidesBase64?: string[]
    ) => Promise<BuildProgress>;
    __pauseBuild: typeof pauseBuild;
    __resumeBuild: typeof resumeBuild;
    __cancelBuild: typeof cancelBuild;
    __clearBuild: typeof clearBuild;
    
    // State access
    __getBuildState: typeof getBuildState;
    __isBuilding: typeof isBuilding;
    __shouldPauseBuild: typeof shouldPause;
    __shouldCancelBuild: typeof shouldCancel;
    
    // Progress callbacks
    __onBuildProgress: (callback: ProgressUpdateCallback) => () => void;
    __onBuildStatusChange: (callback: StatusChangeCallback) => () => void;
    __onBuildError: (callback: ErrorCallback) => () => void;
    
    // API methods
    __builderApi_getState: (planId: string) => Promise<BuildStateResponse>;
    __builderApi_rollback: (planId: string, checkpointId: string) => Promise<RollbackResponse>;
    __builderApi_getCheckpoints: (planId: string) => Promise<CheckpointsResponse>;
    
    // Build jobs list (for UI)
    __getActiveBuildJobs: () => Promise<Array<{
      planId: string;
      planTitle: string;
      status: BuildStatus;
      slideCount: number;
      slidesBuilt: number;
      currentSection?: string;
      currentActivity?: string;
      sections: SectionProgress[];
      progress: number;
      startedAt?: string;
      completedAt?: string;
      errors?: BuildError[];
      seen?: boolean;
    }>>;
    __subscribeToBuildUpdates: (callback: (update: {
      planId: string;
      planTitle?: string;
      status: BuildStatus;
      progress: number;
      slidesBuilt: number;
      totalSlides?: number;
      currentSection?: string;
      currentActivity?: string;
      sections?: SectionProgress[];
      message?: BuildConversationMessage;
    }) => void) => () => void;
    __getBuildMessages: (planId: string) => Promise<BuildConversationMessage[]>;
    
    // Queue management
    __promoteQueuedBuild: (planId: string) => Promise<{
      planId: string;
      planTitle: string;
      status: BuildStatus;
      slideCount: number;
      slidesBuilt: number;
      progress: number;
    } | null>;
    __getQueuePosition: (planId: string) => number | null;
    __removeFromQueue: (planId: string) => Promise<boolean>;
    __clearQueue: () => Promise<void>;
    __getQueueState: () => {
      active: {
        planId: string;
        planTitle: string;
        status: BuildStatus;
        slideCount: number;
        slidesBuilt: number;
        progress: number;
        seen?: boolean;
      } | null;
      queue: Array<{
        planId: string;
        planTitle: string;
        status: BuildStatus;
        slideCount: number;
        slidesBuilt: number;
        progress: number;
      }>;
      completed: Array<{
        planId: string;
        planTitle: string;
        status: BuildStatus;
        slideCount: number;
        slidesBuilt: number;
        progress: number;
        seen?: boolean;
      }>;
    };
    
    // Immediate build (pauses current, starts new)
    __startBuildImmediately: (
      planId: string,
      plan: PresentationPlan,
      styleGuidedocumentId: string,
      styleGuidePath?: string,
      slidesBase64?: string[]
    ) => Promise<BuildProgress>;
    
    // Unseen builds tracking
    __markBuildAsSeen: (planId: string) => boolean;
    __getUnseenCompletedCount: () => number;
  }
}

export {};

